<template>
  <app-content class="h-panel">
    <div class="h-panel-bar">
      <span class="h-panel-title">关联凭证管理</span>
    </div>
    <div class="margin-right-left margin-top">
      <account-date-choose v-model="accountDate" />
      <div class="float-right">
        <Input v-model="keyword" placeholder="凭证号/票据号/回单号 支持模糊" style="width: 260px; margin-right: 8px" @enter="onSearch" />
        <Button :loading="loading" @click="onSearch">查询</Button>
        <Button style="margin-left:8px" @click="showOrphan">列出无效关联</Button>
      </div>
    </div>
    <div class="h-panel-body">
      <div class="table-container">
        <table class="header th-center">
          <tr>
            <td style="width: 160px">凭证</td>
            <td style="width: 200px">方向</td>
            <td>关联对象</td>
            <td style="width: 180px">金额</td>
            <td style="width: 160px">操作</td>
          </tr>
        </table>
        <table v-if="!relations.length">
          <tr>
            <td colspan="5" class="text-center padding">暂无关联</td>
          </tr>
        </table>
        <table class="details" v-for="row in pagedRelations" :key="row.relationId">
          <tr>
            <td style="width: 160px">
              <div class="voucher-info">
                <div class="voucher-label">
                  <router-link :to="{name:'VoucherForm', params:{voucherId: row.voucherId}}">{{ iconForType('VOUCHER') }} 凭证 {{ row.voucherLabel }}</router-link>
                </div>
                <div class="voucher-details" v-if="row.voucherDate || row.voucherAmount">
                  {{ row.voucherDate || '' }}<span v-if="row.voucherAmount"> · 金额: {{ row.voucherAmount | numFormat }}</span>
                </div>
              </div>
            </td>
            <td style="width: 200px">
              <span class="dir-pill">
                <span class="arrow">↔</span>
                <span class="rtype" v-if="row.relationType">{{ relationTypeLabel(row.relationType) }}</span>
                与 {{ row.entityTypeLabel }} 关联
              </span>
            </td>
            <td>
              <div class="entity-info">
                <div class="primary-info">
                  <span class="entity-icon">{{ iconForType(row.entityType) }}</span>
                  <a href="javascript:void(0)" @click="showEntityDetails(row)" class="entity-link">
                    {{ row.entityLabel || row.entityId }}
                  </a>
                </div>
                <div class="secondary-info" v-if="row.entityInfo">
                  {{ row.entityInfo }}
                </div>
              </div>
            </td>
            <td style="width: 180px" class="amount">{{ row.relationAmount | numFormat }}</td>
            <td style="width: 160px" align="right">
              <Button size="small" @click="remove(row)">解除关联</Button>
            </td>
          </tr>
        </table>
      </div>
      <div class="pagination-container">
        <Pagination v-model="relationPagination" @change="onRelationPageChange" layout="pager" small align="center" />
      </div>
      
      <!-- 定时任务状态区域 -->
      <div class="cleanup-job-status">
        <div class="status-card">
          <div class="status-header">
            <h4>🕐 定时清理任务</h4>
            <div class="status-indicator">
              <span class="status-label">状态：</span>
              <span :class="['status-badge', cleanupStatus && cleanupStatus.effectiveEnabled ? 'enabled' : 'disabled']">
                {{ cleanupStatus && cleanupStatus.effectiveEnabled ? '开启' : '关闭' }}
              </span>
            </div>
          </div>
          <div class="status-details">
            <div class="detail-item">
              <span class="label">执行时间：</span>
              <span class="value">{{ cleanupStatus && cleanupStatus.cron || '0 0 3 * * ?' }}（每天凌晨3点）</span>
            </div>
            <div class="detail-item">
              <span class="label">任务说明：</span>
              <span class="value">自动清理指向不存在凭证的关联关系</span>
            </div>
          </div>
          <div class="status-actions">
            <Button 
              :type="cleanupStatus && cleanupStatus.effectiveEnabled ? 'danger' : 'primary'" 
              size="small" 
              @click="toggleCleanup(!cleanupStatus || !cleanupStatus.effectiveEnabled)"
            >
              {{ cleanupStatus && cleanupStatus.effectiveEnabled ? '临时关闭' : '临时开启' }}
            </Button>
            <Button size="small" @click="showOrphan" style="margin-left: 8px">
              列出无效关联
            </Button>
          </div>
        </div>
      </div>
    </div>
    <!-- 实体详情浮窗 -->
    <Modal v-model="showEntityModal" :title="entityModalTitle" width="600">
      <div v-if="selectedEntity" class="entity-details">
        <div class="detail-row">
          <span class="detail-label">类型：</span>
          <span class="detail-value">{{ selectedEntity.typeLabel }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">ID：</span>
          <span class="detail-value">{{ selectedEntity.id }}</span>
        </div>
        <div class="detail-row" v-if="selectedEntity.number">
          <span class="detail-label">编号：</span>
          <span class="detail-value">{{ selectedEntity.number }}</span>
        </div>
        <div class="detail-row" v-if="selectedEntity.date">
          <span class="detail-label">日期：</span>
          <span class="detail-value">{{ selectedEntity.date }}</span>
        </div>
        <div class="detail-row" v-if="selectedEntity.summary">
          <span class="detail-label">摘要：</span>
          <span class="detail-value">{{ selectedEntity.summary }}</span>
        </div>
        <div class="detail-row" v-if="selectedEntity.parties">
          <span class="detail-label">相关方：</span>
          <span class="detail-value">{{ selectedEntity.parties }}</span>
        </div>
        <div class="detail-row" v-if="selectedEntity.amount">
          <span class="detail-label">金额：</span>
          <span class="detail-value amount-highlight">¥{{ selectedEntity.amount | numFormat }}</span>
        </div>
        <div class="detail-row" v-if="selectedEntity.status">
          <span class="detail-label">状态：</span>
          <span class="detail-value">{{ selectedEntity.status }}</span>
        </div>
      </div>
      <div slot="footer">
        <Button @click="showEntityModal = false">关闭</Button>
      </div>
    </Modal>

    <Modal v-model="showCreate" title="新增凭证关联" width="520">
    <div style="display:flex; gap:8px; align-items:center;">
      <Input v-model="form.voucherId" placeholder="凭证ID" style="width: 120px" />
      <Select v-model="form.entityType" style="width: 160px">
        <Option value="DOCUMENT">票据</Option>
        <Option value="DOCUMENT_GROUP">票据组</Option>
        <Option value="RECEIPT">银行回单</Option>
        <Option value="RECEIPT_GROUP">银行回单组</Option>
      </Select>
      <Input v-model="form.entityId" placeholder="实体ID" style="width: 160px" />
    </div>
    <div slot="footer">
      <Button :loading="saving" type="primary" @click="create">保存</Button>
      <Button @click="showCreate=false">取消</Button>
    </div>
    </Modal>
  </app-content>
</template>

<script>
import moment from 'moment'
import { mapState } from 'vuex'

export default {
  name: 'VoucherRelationManagement',
  data() {
    return {
      relations: [],
      loading: false,
      saving: false,
      showCreate: false,
      keyword: '',
      accountDate: null,
      voucherPagination: { page: 1, size: 50, total: 0 },
      relationPagination: { page: 1, total: 0 },
      cleanupStatus: null,
      showEntityModal: false,
      entityModalTitle: '',
      selectedEntity: null,
      form: {
        voucherId: '',
        entityType: 'RECEIPT',
        entityId: ''
      },
      // 本地缓存，避免重复请求
      _billCache: {},
      _receiptCache: {},
      _docGroupCache: {},
      _receiptGroupCache: {}
    }
  },
  computed: {
    ...mapState(['User', 'currentAccountSets']),
    date() { return moment(this.accountDate) },
    // 关键字过滤（支持：凭证字号、票据号、回单号、组名、实体ID）
    visibleRelations(){
      const kw = (this.keyword || '').trim().toLowerCase()
      if(!kw) return this.relations
      return this.relations.filter(r => {
        const fields = [
          r.voucherLabel,
          r.entityLabel,
          r.entityId,
          r.entityTypeLabel,
          r.entityInfo,
          r.voucherDate
        ].filter(Boolean).map(String).map(s=>s.toLowerCase())
        return fields.some(s => s.includes(kw))
      })
    },
    pagedRelations(){
      const list = this.visibleRelations
      const page = Math.max(1, this.relationPagination.page || 1)
      const size = 10
      const start = (page - 1) * size
      return list.slice(start, start + size)
    }
  },
  filters: {
    numFormat(v){
      if(v==null) return ''
      return Number(v).toLocaleString('zh-CN', {minimumFractionDigits: 2, maximumFractionDigits: 2})
    }
  },
  methods: {
    iconForType(t){
      switch(t){
        case 'VOUCHER': return '🧾'
        case 'DOCUMENT': return '📄'
        case 'DOCUMENT_GROUP': return '📋'
        case 'RECEIPT': return '🏦'
        case 'RECEIPT_GROUP': return '🗂'
        default: return '🔗'
      }
    },
    relationTypeLabel(t){
      switch(t){
        case 'AI_GENERATED': return 'AI生成'
        case 'MANUAL': return '手工维护'
        default: return '关联'
      }
    },
    currentChange(){ this.loadList() },
    onRelationPageChange(){},
    async loadList(){
      this.loading = true
      try {
        // 使用去重集合，避免重复加入相同 relationId
        const seen = new Set()
        const merged = []

        // A) 优先并入“孤儿关联”（即指向不存在凭证的关联），保证凭证删除后也能显示
        try {
          const orphanResp = await fetch('/api/relations/orphan-relations', { credentials:'include' })
          const orphanJson = await orphanResp.json()
          const orphans = (orphanJson && orphanJson.success && orphanJson.data) ? orphanJson.data : []
          orphans.forEach(r => {
            if (seen.has(r.relationId)) return
            if (r.sourceType!=='VOUCHER' && r.targetType!=='VOUCHER') return
            const isVoucherSource = r.sourceType==='VOUCHER'
            const other = isVoucherSource ? {type:r.targetType, id:r.targetId} : {type:r.sourceType, id:r.sourceId}
            if (!other.type || other.type==='VOUCHER') return
            const orphanVoucherId = isVoucherSource ? r.sourceId : r.targetId
            merged.push({
              relationId: r.relationId,
              voucherId: orphanVoucherId,
              voucherLabel: `凭证已删除(ID: ${orphanVoucherId})`,
              voucherDate: null,
              voucherAmount: null,
              relationType: r.relationType,
              entityType: other.type,
              entityTypeLabel: this.typeLabel(other.type),
              entityId: String(other.id),
              entityLabel: null,
              relationAmount: r.relationAmount,
              entityInfo: ''
            })
            seen.add(r.relationId)
          })
        } catch(e) {
          // 忽略孤儿关联失败
        }

        // B) 查询当期所有凭证列表并并入其跨类型关联
        const res = await this.$api.voucher.list({voucher_year: this.date.year(), voucher_month: this.date.month()+1, page: this.voucherPagination.page})
        const vouchers = (res && res.data && res.data.records) || []

        // 2) 并发拉取每个凭证的跨类型关联
        await Promise.all(vouchers.map(async v => {
          try {
            const q = await fetch(`/api/relations/all/${v.id}?entityType=VOUCHER`, { credentials: 'include' })
            const jr = await q.json()
            const list = (jr && jr.data) || []
            list.forEach(r => {
              if (seen.has(r.relationId)) return
              if (r.sourceType!=='VOUCHER' && r.targetType!=='VOUCHER') return
              const other = r.sourceType==='VOUCHER' ? {type:r.targetType, id:r.targetId} : {type:r.sourceType, id:r.sourceId}
              if (!other.type || other.type==='VOUCHER') return
              merged.push({
                relationId: r.relationId,
                voucherId: v.id,
                voucherLabel: `${v.word}-${v.code}`,
                voucherDate: v.voucherDate,
                voucherAmount: v.debitAmount || v.creditAmount,
                relationType: r.relationType,
                entityType: other.type,
                entityTypeLabel: this.typeLabel(other.type),
                entityId: String(other.id),
                entityLabel: null,
                relationAmount: r.relationAmount,
                entityInfo: ''
              })
              seen.add(r.relationId)
            })
          } catch(e) {}
        }))

        // C) 兜底：如果仍然没有数据，则直接拉取全量跨类型关系（VOUCHER 相关）
        if (!merged.length) {
          try {
            const resp = await fetch('/api/relations/cross-type?entityType=VOUCHER', { credentials:'include' })
            const jr = await resp.json()
            const all = (jr && jr.data) || []
            // 建立凭证映射以判断是否被删除
            const voucherMap = {}
            vouchers.forEach(v => { voucherMap[String(v.id)] = v })
            all.forEach(r => {
              if (seen.has(r.relationId)) return
              if (r.sourceType!=='VOUCHER' && r.targetType!=='VOUCHER') return
              const isVoucherSource = r.sourceType==='VOUCHER'
              const other = isVoucherSource ? {type:r.targetType, id:r.targetId} : {type:r.sourceType, id:r.sourceId}
              if (!other.type || other.type==='VOUCHER') return
              const vid = String(isVoucherSource ? r.sourceId : r.targetId)
              const v = voucherMap[vid]
              merged.push({
                relationId: r.relationId,
                voucherId: vid,
                voucherLabel: v ? `${v.word}-${v.code}` : `凭证已删除(ID: ${vid})`,
                voucherDate: v && v.voucherDate,
                voucherAmount: v && (v.debitAmount || v.creditAmount),
                relationType: r.relationType,
                entityType: other.type,
                entityTypeLabel: this.typeLabel(other.type),
                entityId: String(other.id),
                entityLabel: null,
                relationAmount: r.relationAmount,
                entityInfo: ''
              })
              seen.add(r.relationId)
            })
          } catch(e) {
            // 忽略兜底失败
          }
        }
        // 解析实体可读标签（票据号/回单号/组名），再赋值
        await this.resolveEntityLabels(merged)
        this.relations = merged
        this.voucherPagination.total = (res && res.data && res.data.total) || 0
        this.relationPagination.total = this.visibleRelations.length
      } finally {
        this.loading = false
      }
    },
    async resolveEntityLabels(items){
      // 确保缓存已初始化
      if (!this._billCache) this._billCache = {}
      if (!this._receiptCache) this._receiptCache = {}
      if (!this._docGroupCache) this._docGroupCache = {}
      if (!this._receiptGroupCache) this._receiptGroupCache = {}

      const tasks = items.map(async it => {
        const id = it.entityId
        try{
          switch(it.entityType){
            case 'DOCUMENT': {
              if (!this._billCache[id]) {
                const resp = await this.$api.bill.load(id)
                this._billCache[id] = resp && resp.data || null
              }
              const b = this._billCache[id]
              const number = b && (b.billNo || b.invoiceNumber)
              it.entityLabel = number || id
              if (b) {
                const parts = []
                if (b.billDate) parts.push(b.billDate)
                if (b.summary) parts.push(`摘要: ${b.summary}`)
                if (b.issuer) parts.push(`开票方: ${b.issuer}`)
                if (b.recipient) parts.push(`收票方: ${b.recipient}`)
                if (b.amount != null) parts.push(`金额: ${Number(b.amount).toFixed(2)}`)
                it.entityInfo = parts.join(' · ')
              }
              break
            }
            case 'RECEIPT': {
              if (!this._receiptCache[id]) {
                const resp = await this.$api.bankReceipts.load(id)
                this._receiptCache[id] = resp && resp.data || null
              }
              const r = this._receiptCache[id]
              const number = r && r.receiptsNo
              it.entityLabel = number || id
              if (r) {
                const parts = []
                if (r.receiptsDate) parts.push(r.receiptsDate)
                if (r.summary) parts.push(`摘要: ${r.summary}`)
                // 根据类型确定对方信息
                let counterparty = ''
                if (r.type === '支出') {
                  counterparty = r.payeeName ? `收款方: ${r.payeeName}` : ''
                } else if (r.type === '收入') {
                  counterparty = r.payerName ? `付款方: ${r.payerName}` : ''
                } else {
                  counterparty = r.payeeName || r.payerName ? `对方: ${r.payeeName || r.payerName}` : ''
                }
                if (counterparty) parts.push(counterparty)
                if (r.amount != null) parts.push(`金额: ${Number(r.amount).toFixed(2)}`)
                it.entityInfo = parts.join(' · ')
              }
              break
            }
            case 'DOCUMENT_GROUP': {
              if (!this._docGroupCache[id]) {
                const resp = await fetch(`/api/merge-groups/documents/${id}`, { credentials:'include' })
                const jr = await resp.json()
                this._docGroupCache[id] = jr && jr.data || null
              }
              const g = this._docGroupCache[id]
              it.entityLabel = (g && (g.groupName || g.name || `票据组#${id}`)) || `票据组#${id}`
              if (g) {
                const parts = []
                if (g.createdAt) parts.push(g.createdAt)
                if (g.totalAmount != null) parts.push(`合计: ${Number(g.totalAmount).toFixed(2)}`)
                if (g.summary) parts.push(`摘要: ${g.summary}`)
                it.entityInfo = parts.join(' · ')
              }
              break
            }
            case 'RECEIPT_GROUP': {
              if (!this._receiptGroupCache[id]) {
                const resp = await fetch(`/api/merge-groups/receipts/${id}`, { credentials:'include' })
                const jr = await resp.json()
                this._receiptGroupCache[id] = jr && jr.data || null
              }
              const g = this._receiptGroupCache[id]
              it.entityLabel = (g && (g.groupName || g.name || `回单组#${id}`)) || `回单组#${id}`
              if (g) {
                const parts = []
                if (g.createdAt) parts.push(g.createdAt)
                if (g.totalAmount != null) parts.push(`合计: ${Number(g.totalAmount).toFixed(2)}`)
                if (g.summary) parts.push(`摘要: ${g.summary}`)
                it.entityInfo = parts.join(' · ')
              }
              break
            }
          }
        }catch(e){
          it.entityLabel = it.entityLabel || id
        }
      })
      await Promise.all(tasks)
      this.relationPagination.total = this.visibleRelations.length
    },
    typeLabel(t){
      switch(t){
        case 'DOCUMENT': return '票据'
        case 'DOCUMENT_GROUP': return '票据组'
        case 'RECEIPT': return '银行回单'
        case 'RECEIPT_GROUP': return '银行回单组'
        default: return t
      }
    },
    onSearch(){
      // 只做前端过滤，不清空数据；若需要服务端过滤可扩展voucher.list的查询条件
      // 这里直接依赖computed: visibleRelations
    },
    async showOrphan(){
      try{
        const resp = await fetch('/api/relations/orphan-relations', { credentials:'include' })
        const jr = await resp.json()
        if(jr && jr.success){
          const data = jr.data || []
          if(!data.length){ this.$Message.success('当前没有无效关联'); return }
          // 简单弹窗展示数量，并在控制台打印详情供排查
          console.table(data)
          this.$Modal.info({ title:'无效关联', content:`共 ${data.length} 条，详情已打印至控制台（F12）` })
        } else {
          this.$Message.error(jr && jr.msg || '查询失败')
        }
      } catch(e){
        this.$Message.error('查询失败: ' + e.message)
      }
    },
    async loadCleanupStatus(){
      try{
        const r = await fetch('/api/relations/cleanup-job/status', { credentials:'include' })
        const jr = await r.json()
        if(jr && jr.success){ this.cleanupStatus = jr.data }
      }catch(e){ /* 静默失败 */ }
    },
    async toggleCleanup(enabled){
      try{
        const r = await fetch(`/api/relations/cleanup-job/toggle?enabled=${enabled}`, { method:'POST', credentials:'include' })
        const jr = await r.json()
        if(jr && jr.success){
          this.$Message.success('已更新定时任务状态')
          this.loadCleanupStatus()
        } else {
          this.$Message.error(jr && jr.msg || '操作失败')
        }
      }catch(e){ this.$Message.error('操作失败: ' + e.message) }
    },
    async showEntityDetails(row){
      const entityType = row.entityType
      const entityId = row.entityId
      
      this.selectedEntity = {
        id: entityId,
        typeLabel: row.entityTypeLabel,
        type: entityType
      }
      
      try {
        // 根据实体类型获取详细信息
        switch(entityType) {
          case 'DOCUMENT': {
            // 确保缓存已初始化
            if (!this._billCache) this._billCache = {}
            if (!this._billCache[entityId]) {
              const resp = await this.$api.bill.load(entityId)
              this._billCache[entityId] = resp && resp.data || null
            }
            const bill = this._billCache[entityId]
            if (bill) {
              this.selectedEntity = {
                ...this.selectedEntity,
                number: bill.billNo || bill.invoiceNumber,
                date: bill.billDate,
                summary: bill.summary,
                parties: [bill.sellerName, bill.buyerName].filter(Boolean).join(' → '),
                amount: bill.amount,
                status: bill.status || '正常'
              }
            }
            this.entityModalTitle = `📄 票据详情 - ${this.selectedEntity.number || entityId}`
            break
          }
          case 'RECEIPT': {
            // 确保缓存已初始化
            if (!this._receiptCache) this._receiptCache = {}
            if (!this._receiptCache[entityId]) {
              const resp = await this.$api.bankReceipts.load(entityId)
              this._receiptCache[entityId] = resp && resp.data || null
            }
            const receipt = this._receiptCache[entityId]
            if (receipt) {
              // 根据类型确定交易对手
              let parties = ''
              if (receipt.type === '支出') {
                parties = receipt.payeeName ? `付款给：${receipt.payeeName}` : '未知收款方'
              } else if (receipt.type === '收入') {
                parties = receipt.payerName ? `来自：${receipt.payerName}` : '未知付款方'
              } else {
                parties = receipt.payeeName || receipt.payerName || '未知交易对手'
              }
              
              this.selectedEntity = {
                ...this.selectedEntity,
                number: receipt.receiptsNo,
                date: receipt.receiptsDate,
                summary: receipt.summary,
                parties: parties,
                amount: receipt.amount,
                status: receipt.status || '正常'
              }
            }
            this.entityModalTitle = `🏦 银行回单详情 - ${this.selectedEntity.number || entityId}`
            break
          }
          case 'DOCUMENT_GROUP': {
            // 确保缓存已初始化
            if (!this._docGroupCache) this._docGroupCache = {}
            if (!this._docGroupCache[entityId]) {
              const resp = await fetch(`/api/merge-groups/documents/${entityId}`, { credentials:'include' })
              const jr = await resp.json()
              this._docGroupCache[entityId] = jr && jr.data || null
            }
            const group = this._docGroupCache[entityId]
            if (group) {
              this.selectedEntity = {
                ...this.selectedEntity,
                number: group.groupName || group.name || `票据组#${entityId}`,
                date: group.createdAt,
                summary: group.summary,
                amount: group.totalAmount,
                status: `包含${group.billCount || 0}张票据`
              }
            }
            this.entityModalTitle = `📁 票据组详情 - ${this.selectedEntity.number || entityId}`
            break
          }
          case 'RECEIPT_GROUP': {
            // 确保缓存已初始化
            if (!this._receiptGroupCache) this._receiptGroupCache = {}
            if (!this._receiptGroupCache[entityId]) {
              const resp = await fetch(`/api/merge-groups/receipts/${entityId}`, { credentials:'include' })
              const jr = await resp.json()
              this._receiptGroupCache[entityId] = jr && jr.data || null
            }
            const group = this._receiptGroupCache[entityId]
            if (group) {
              this.selectedEntity = {
                ...this.selectedEntity,
                number: group.groupName || group.name || `回单组#${entityId}`,
                date: group.createdAt,
                summary: group.summary,
                amount: group.totalAmount,
                status: `包含${group.receiptCount || 0}张回单`
              }
            }
            this.entityModalTitle = `📁 银行回单组详情 - ${this.selectedEntity.number || entityId}`
            break
          }
        }
        
        this.showEntityModal = true
      } catch(e) {
        console.error('加载实体详情失败', e)
        this.$Message.error('加载详情失败')
      }
    },
    async remove(row){
      // 仅解除单条关联，不影响凭证本身
      await this.$Confirm('确认解除该凭证关联？')
      await fetch(`/api/relations/${row.relationId}`, { method:'DELETE', credentials:'include' })
      this.$Message.success('已解除关联')
      this.loadList()
    },
    async create(){
      this.saving = true
      try{
        const payload = {
          sourceType: this.form.entityType,
          sourceId: String(this.form.entityId),
          targetType: 'VOUCHER',
          targetId: String(this.form.voucherId),
          relationType: 'MANUAL'
        }
        const res = await fetch('/api/relations', {
          method:'POST',
          headers:{'Content-Type':'application/json'},
          credentials: 'include',
          body: JSON.stringify(payload)
        })
        const jr = await res.json()
        if(jr && jr.success){
          this.$Message.success('保存成功')
          this.showCreate = false
          this.loadList()
        }else{
          this.$Message.error(jr && jr.msg || '保存失败')
        }
      } finally {
        this.saving = false
      }
    }
  },
  watch:{
    accountDate(){ this.relationPagination.page=1; this.loadList() }
  },
  mounted(){
    this.accountDate = this.currentAccountSets && this.currentAccountSets.currentAccountDate
    if(!this.accountDate){ this.accountDate = moment().format('YYYY-MM-01') }
    this.loadList()
    this.loadCleanupStatus()
  }
}
</script>

<style lang="less" scoped>
/* 美化样式，参考 AiAnalysisHistory 的卡片风格 */
.h-panel-body {
  padding: 20px;
  
  .float-right {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    
    .job-status {
      margin-left: 12px;
      color: #909399;
      font-size: 12px;
      white-space: nowrap;
      
      .green { color: #67C23A; }
      .red { color: #F56C6C; }
    }
  }
  
  /* 表格容器卡片风格 */
  .table-container {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    box-shadow: 0 1px 2px rgba(0,0,0,.03);
    overflow: hidden;
  }
  
  /* 表格样式 */
  table { 
    width: 100%; 
    border-collapse: collapse; 
    background: #fff;
  }
  
  table.header { 
    background-color: @primary-color; 
    color: #fff; 
  }
  
  .th-center td { 
    text-align: center; 
    font-weight: 600;
    background: #fafafa;
    color: #555;
    border-bottom: 1px solid #f0f0f0;
    padding: 12px 8px;
    font-size: 13px;
  }
  
  .details { 
    border: none;
    margin: 0;
    background: #fff;
    
    tr:hover {
      background: #fafafa;
    }
    
    td {
      padding: 12px 8px;
      border-bottom: 1px solid #f5f5f5;
      font-size: 13px;
      vertical-align: top;
    }
  }
  
  /* 方向标签胶囊样式 */
  .dir-pill { 
    display: inline-flex; 
    align-items: center; 
    gap: 6px; 
    padding: 4px 8px; 
    border-radius: 12px; 
    background: #f5f7fa; 
    color: #606266; 
    font-size: 12px;
    white-space: nowrap;
    
    .arrow { 
      font-weight: 700; 
      color: #409EFF; 
    }
  }
  
  /* 实体类型图标 */
  .entity-icon {
    margin-right: 6px;
    font-size: 14px;
  }
  
  /* 实体信息展示 */
  .entity-info {
    .primary-info {
      font-weight: 600;
      color: #333;
      margin-bottom: 2px;
    }
    
    .secondary-info {
      color: #666;
      font-size: 12px;
      line-height: 1.3;
    }
  }
  
  /* 凭证信息展示 */
  .voucher-info {
    .voucher-label {
      font-weight: 600;
      color: #333;
      margin-bottom: 2px;
    }
    
    .voucher-details {
      color: #666;
      font-size: 12px;
    }
  }
  
  /* 金额格式 */
  .amount {
    text-align: right;
    font-family: "Courier New", monospace;
    font-weight: 600;
  }
  
  /* 分页容器 */
  .pagination-container {
    background: #fff;
    padding: 16px;
    border-top: 1px solid #f0f0f0;
    text-align: center;
  }
  
  /* 实体链接样式 */
  .entity-link {
    color: #1890ff;
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      color: #40a9ff;
      text-decoration: underline;
    }
  }
  
  /* 定时任务状态区域 */
  .cleanup-job-status {
    margin-top: 20px;
    
    .status-card {
      background: #fff;
      border: 1px solid #f0f0f0;
      border-radius: 8px;
      padding: 16px;
      box-shadow: 0 1px 2px rgba(0,0,0,.03);
    }
    
    .status-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      h4 {
        margin: 0;
        font-size: 16px;
        color: #333;
      }
      
      .status-indicator {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      
      .status-label {
        color: #666;
        font-size: 14px;
      }
      
      .status-badge {
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        
        &.enabled {
          background: #f6ffed;
          color: #52c41a;
          border: 1px solid #b7eb8f;
        }
        
        &.disabled {
          background: #fff2f0;
          color: #ff4d4f;
          border: 1px solid #ffccc7;
        }
      }
    }
    
    .status-details {
      margin-bottom: 16px;
      
      .detail-item {
        display: flex;
        margin-bottom: 8px;
        
        .label {
          color: #666;
          width: 80px;
          flex-shrink: 0;
        }
        
        .value {
          color: #333;
          flex: 1;
        }
      }
    }
    
    .status-actions {
      text-align: right;
    }
  }
  
  /* 实体详情浮窗样式 */
  .entity-details {
    .detail-row {
      display: flex;
      margin-bottom: 12px;
      padding: 8px 0;
      border-bottom: 1px solid #f5f5f5;
      
      &:last-child {
        border-bottom: none;
      }
    }
    
    .detail-label {
      width: 80px;
      color: #666;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .detail-value {
      flex: 1;
      color: #333;
      word-break: break-all;
      
      &.amount-highlight {
        color: #52c41a;
        font-weight: 600;
        font-family: "Courier New", monospace;
      }
    }
  }
}
</style>


